import { useEffect, useState } from "react";
import { getEdgeFunctionUrl } from "../../lib/api-url";
import { captureEvent } from "../../lib/analytics";

// Type definitions
interface PrivacySettings {
  hide_message: boolean;
  hide_score: boolean;
  hide_summary: boolean;
  hide_narrator: boolean;
  hide_snippets: boolean;
  hide_rewrites: boolean;
}

interface ExplicitSharedResult {
  link_type: 'explicit_result';
  score: number | null;
  summary: string | null;
}

interface SharedAnalysisResponse {
  data?: Record<string, unknown> & {
    analysis_id?: string | null;
    privacy_settings?: PrivacySettings | null;
    link_type?: string;
    score?: number | null;
    summary?: string | null;
  };
  error?: string;
  code?: string | null;
}

interface AnalysisData {
  title?: string;
  description?: string;
  score?: number;
}

interface UseSharedLinkDataResult {
  error: string | null;
  isValidating: boolean;
  validatedAnalysisId: string | null;
  analysisData: AnalysisData | null;
  explicitResult: ExplicitSharedResult | null;
}

/**
 * Validate short_id format:
 * - Legacy: 10–22 base64url chars
 * - Explicit result: 43 base64url chars (a 32-byte opaque bearer token)
 */
function isValidShortId(id: string): boolean {
  return /^(?:[0-9A-Za-z_-]{10,22}|[0-9A-Za-z_-]{43})$/.test(id);
}

function parseErrorType(errorMessage: string): string {
  if (errorMessage.includes("not found") || errorMessage === "not_found") return "not_found";
  if (errorMessage.includes("expired")) return "expired";
  if (errorMessage.includes("revoked")) return "revoked";
  return "unknown";
}

/**
 * Hook to handle shared link data validation and fetching.
 *
 * Calls get-shared-analysis once — which validates the token, increments the
 * view counter, decrypts the analysis, and returns all data needed for rendering
 * and OG tags. Uses direct fetch() to data-proxy RPC endpoint.
 */
export function useSharedLinkData(shortId: string | undefined): UseSharedLinkDataResult {
  const hasValidInitialToken = Boolean(shortId && isValidShortId(shortId));
  const [error, setError] = useState<string | null>(() => hasValidInitialToken ? null : "invalid_format");
  const [isValidating, setIsValidating] = useState(() => hasValidInitialToken);
  const [validatedAnalysisId, setValidatedAnalysisId] = useState<string | null>(null);
  const [analysisData, setAnalysisData] = useState<AnalysisData | null>(null);
  const [explicitResult, setExplicitResult] = useState<ExplicitSharedResult | null>(null);

  // The async request is guarded by AbortController and isMounted below; the local
  // heuristic cannot recognize the combined cancellation pattern.
  // eslint-disable-next-line local-rules/no-race-conditions
  useEffect(() => {
    // Client-side format validation
    if (!shortId || !isValidShortId(shortId)) {
      // Track invalid format event
      captureEvent("Share_ShortUrl_InvalidFormat", { source: "client_validation" });
      return;
    }

    const controller = new AbortController();
    let isMounted = true;

    // Single call: validate token + increment view + fetch analysis data
    const validateAndRedirect = async () => {
      try {
        const analysisResponse = await fetch(
          `${getEdgeFunctionUrl('get-shared-analysis')}?token=${encodeURIComponent(shortId)}`,
          {
            signal: controller.signal,
            headers: { 'Accept-Version': 'v2' },
          },
        );

        if (!analysisResponse.ok) {
          const errBody = await analysisResponse.json().catch(() => ({})) as { error?: string; code?: string };
          if (!isMounted) return;
          const errorMessage = errBody.code || errBody.error || "unknown";

          if (analysisResponse.status >= 500) {
            captureEvent("Share_ShortUrl_RpcError", { failure_class: "server" });
            setError("rpc_error");
          } else {
            const errorType = parseErrorType(errorMessage);
            captureEvent("Share_ShortUrl_ValidationFailed", { error_type: errorType });
            setError(errorType);
          }
          setIsValidating(false);
          return;
        }

        const { data } = await analysisResponse.json() as SharedAnalysisResponse;
        if (!isMounted) return;

        if (!data) {
          setError("unknown");
          setIsValidating(false);
          return;
        }

        const isExplicitResult = data.link_type === 'explicit_result';
        const analysisId = isExplicitResult
          ? null
          : data.analysis_id ?? (data.id as string | undefined) ?? null;
        const privacySettings = data.privacy_settings ?? null;
        const hideScore = privacySettings?.hide_score ?? false;
        const hideSummary = privacySettings?.hide_summary ?? false;
        const ogDescription = "AI-powered communication analysis. Detect drama, passive-aggressive patterns, and improve your messages.";

        setAnalysisData({
          title: isExplicitResult
            ? data.score === null || data.score === undefined ? "Drama Analysis" : `Drama Score: ${data.score}/10`
            : hideScore ? "Drama Analysis" : `Drama Score: ${data.score}/10`,
          description: isExplicitResult
            ? data.summary || ogDescription
            : hideSummary ? ogDescription : ((data.summary as string) || ogDescription),
          score: isExplicitResult
            ? (data.score as number | undefined)
            : hideScore ? undefined : (data.score as number | undefined),
        });

        setExplicitResult(isExplicitResult ? {
          link_type: 'explicit_result',
          score: typeof data.score === 'number' ? data.score : null,
          summary: typeof data.summary === 'string' ? data.summary : null,
        } : null);

        // Public-link telemetry is aggregate-only: never record bearer tokens or source identifiers.
        captureEvent("Share_ShortUrl_Success", {
          link_type: isExplicitResult ? 'explicit_result' : 'legacy',
          has_score: data.score !== null && data.score !== undefined,
          has_summary: typeof data.summary === 'string' && data.summary.length > 0,
        });

        setValidatedAnalysisId(analysisId);
        setIsValidating(false);
      } catch (err) {
        if (!isMounted || (err instanceof DOMException && err.name === 'AbortError')) return;
        captureEvent("Share_ShortUrl_UnexpectedError", {});

        setError("unexpected");
        setIsValidating(false);
      }
    };

    validateAndRedirect();
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [shortId]);

  return {
    error,
    isValidating,
    validatedAnalysisId,
    analysisData,
    explicitResult,
  };
}
